<template>
  <div v-if="surveillance" id="establishment-results">
    <div class="row">
      <div class="col-xs-12">
        <h2>Résultats SPIADI {{ surveillance.year }}</h2>
        <hr />
      </div>
      <div v-if="!hasAccess">
        <div class="col-lg-9 col-xs-12">
          <alert type="warning" class="text-justify">
            <h4>
              Accès non autorisé aux résultats de l'établissement
              «{{ establishment }}»
            </h4>
            <p>
              Pour des raisons de confidentialité (CNIL), les résultats
              {{ surveillance.year }} sont, par défaut, uniquement accessibles
              pour le responsable du programme.
            </p>
            <p>
              <strong>
                Pour obtenir vos résultats, merci de bien vouloir contacter le
                responsable du programme (personne ayant effectué
                l'inscription) pour votre établissement :
                {{ surveillance.referentName }}.
              </strong>
            </p>
            <p>
              N'hésitez pas à revenir vers nous via le
              <router-link to="/contact">formulaire de contact</router-link> si
              vous souhaitez avoir plus de précisions.
            </p>
          </alert>
        </div>
      </div>
      <div v-else>
        <div class="col-xs-12"
             v-if="results != null && !results.empty">
          <alert type="warning" class="text-justify">
            <h4>
              Bienvenue sur l'espace résultats du programme SPIADI
              {{ surveillance.year }}
            </h4>
            <p>
              Vous trouverez sur cette page les taux d'incidence des
              bactériémies et des pneumopathies calculés à partir des données de
              votre établissement
              {{ surveillance.year === 2019 ? '(figées au 23&nbsp;août&nbsp;2019)' : '' }}.
            </p>
            <p>
              <btn type="primary" @click="help = true">
                Comment lire les résultats
                <span class="glyphicon glyphicon-question-sign"
                      style="margin-left: 0.3rem"
                      aria-hidden="true"></span>
              </btn>
            </p>
          </alert>
          <alert type="info" class="text-justify" v-if="isRegistrator">
            <p>
              En tant que responsable, vous pouvez ajouter ou supprimer des
              destinataires supplémentaires.
            </p>
            <p>
              <btn type="default" @click="members = true">
                Gérer les destinataires des résultats
              </btn>
            </p>
            <modal v-model="members"
                   :footer="false"
                   size="lg"
                   title="Destinataires des résultats">
              <sui-members-table :surveillance="surveillance"
                                 module="results"
                                 nested
                                 :roles="roles">
              </sui-members-table>
            </modal>
          </alert>
          <modal v-model="help"
                 :footer="false"
                 size="lg"
                 title="Informations sur les résultats">
            <how-to-read-results></how-to-read-results>
          </modal>
          <sui-results-dashboard :results="results"
                                 :year="surveillance.year"
                                 :chartTitle="chartTitle">
          </sui-results-dashboard>
        </div>
        <div class="col-xs-12" v-else-if="results != null">
          <alert type="warning" class="text-justify">
            <h4>
              Aucun résultat n'a été trouvé pour «{{ establishment }}»
            </h4>
            <p>
              Les données de votre établissement n'ont pas pû être prises en
              compte dans les données de la surveillance {{ surveillance.year }}, pour
              l'une des raisons suivante :
              <ul>
                <li>La surveillance n'a pas été clôturée</li>
                <li v-if="surveillance.year === 2019">
                  La surveillance a été clôturée après le 23&nbsp;août&nbsp;2019
                </li>
                <li v-if="surveillance.year === 2019">
                  Lors du gel des données le 23&nbsp;août&nbsp;2019, la fiche
                  établissement ne comportait pas les dénominateurs, empêchant
                  le calcul des taux d'incidence.
                </li>
              </ul>
            </p>
            <p>
              Si vous avez rempli et validé des fiches, vous pouvez
              vous rendre sur l'espace «Données» pour les exporter en csv. Sur
              cette page, vous pourrez à l'avenir explorer les données et
              générer des graphiques.
            </p>
            <p>
              N'hésitez pas à revenir vers nous via le
              <router-link to="/contact">formulaire de contact</router-link> si
              vous souhaitez avoir plus de précisions.
            </p>
          </alert>
        </div>
        <div class="col-xs-12" v-else-if="error.error">
          <sui-error-panel :error="error">
          </sui-error-panel>
        </div>
        <div class="col-xs-12" v-else>
          <sui-loading-text>
            <span>Chargement des résultats en cours, merci de patienter</span>
          </sui-loading-text>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import suiErrorPanel from '@/sui/components/errorpanel'
import suiLoadingText from '@/sui/components/loadingtext'
import suiResultsDashboard from '@/sui/components/results/dashboard'
import suiIncidence from '@/sui/components/results/incidence'
import suiMembersTable from '@/sui/components/members/table2019'
import readResults from '@/sui/components/results/readResults'
import Error from '@/sui/tools/error'

import { mapGetters } from 'vuex'

export default {
  name: 'establishment-results',

  props: {
    surveillance: {
      type: Object
    }
  },

  data: function () {
    return {
      members: false,
      roles: [
        {
          name: 'Responsable',
          value: -1,
          selectable: false
        },
        {
          name: 'Destinataire des résultats',
          value: 1,
          selectable: true
        }
      ],

      help: false,

      error: new Error()
    }
  },

  methods: {
    loadResults: function () {
      if (this.hasAccess) {
        this.error.reset()
        this.$store.dispatch(
          'results/loadResult', { s: this.id }
        ).then(
          r => {
            if (r.data.err != null && r.data.err !== '[NoResults]') {
              this.setError()
            }
            return r
          }
        ).catch(
          (e) => {
            this.setError()
          }
        )
      }
    },

    setError: function () {
      this.error.set(
        'Erreur interne !',
        'Erreur lors de la récupération des résultats',
        'Merci de vérifier votre connexion internet avant de réessayer.'
      )
    }
  },

  computed: {
    ...mapGetters({
      getResult: 'results/getResult',
      getStatistics: 'results/getStatistics',
      userId: 'user/id'
    }),

    id: function () {
      return this.surveillance.id
    },

    isRegistrator: function () {
      if (this.surveillance == null) {
        return false
      }

      return this.surveillance.isRegistrator(this.userId)
    },

    hasAccess: function () {
      if (this.surveillance == null) {
        return false
      }

      return this.surveillance.isMember(this.userId, 'results')
    },

    results: function () {
      return this.getResult(this.id)
    },

    establishment: function () {
      return this.surveillance.establishment.name
    },

    chartTitle: function () {
      return `${this.establishment} - SPIADI ${this.surveillance.year}`
    }
  },

  watch: {
    surveillance: function (val, old) {
      if (val !== old) {
        this.loadResults()
      }
    }
  },

  mounted: function () {
    if (this.surveillance != null) {
      this.loadResults()
    }
  },

  components: {
    'sui-error-panel': suiErrorPanel,
    'sui-loading-text': suiLoadingText,
    'sui-results-dashboard': suiResultsDashboard,
    'sui-members-table': suiMembersTable,
    'sui-incidence': suiIncidence,
    'how-to-read-results': readResults
  }
}
</script>
<style scoped lang="less">
#establishment-results {
  min-height: 100vl;
}
</style>
